import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiFetch, apiJson, assetUrl } from "../utils/api";
import { CARD_TEMPLATES, normalizeTemplate, paintHarvestCardAsync } from "../utils/harvestCard";
import SpeechInputButton from "../components/SpeechInputButton";
import useSeo from "../hooks/useSeo";

const PROMPTS = [
  "今天最触动我的一句话",
  "我对爱情/婚姻的新理解",
  "今天我想到的一个问题",
  "今天我最想记住的一句话",
];

export default function ReadingCheckinPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const canvasRef = useRef(null);
  const photoInputRef = useRef(null);
  const [view, setView] = useState(null);
  const [text, setText] = useState("");
  const [aiText, setAiText] = useState("");
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [template, setTemplate] = useState("dawn");
  const [headerPhotoUrl, setHeaderPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shareHint, setShareHint] = useState("");
  const [completed, setCompleted] = useState(false);

  useSeo({
    title: "今日晨读收获",
    metaDescription: "把今天读书后的一句话，整理成属于自己的分享卡片。",
    path: "/reading/checkin",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/reading/checkin")}`, {
        replace: true,
      });
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    const [todayRes, histRes] = await Promise.all([
      apiJson(`/api/reading/checkin/today?user_id=${userId}`),
      apiJson(`/api/reading/checkin/history?user_id=${userId}&limit=8`),
    ]);
    if (todayRes.res.ok) {
      setView(todayRes.data);
      const cur = todayRes.data.item;
      if (cur) {
        setItem(cur);
        setText(cur.original_text || "");
        setAiText(cur.ai_text || "");
        setCompleted(!!cur.is_completed);
        if (cur.card_template) setTemplate(normalizeTemplate(cur.card_template));
        if (cur.card_image_url) setHeaderPhotoUrl(cur.card_image_url);
      }
    }
    if (histRes.res.ok) setHistory(histRes.data.items || []);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showCard || !canvasRef.current || !aiText) return;
    let cancelled = false;
    (async () => {
      await paintHarvestCardAsync(canvasRef.current, {
        text: aiText,
        bookTitle: view?.book?.title || item?.book_title || "",
        template,
        dateLabel: view?.date_label || item?.reading_date || "",
        headerImageUrl: headerPhotoUrl ? assetUrl(headerPhotoUrl) : null,
      });
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [showCard, aiText, template, headerPhotoUrl, view, item]);

  const generate = async (regenerate = false) => {
    if (!text.trim()) {
      setError("写下一两句话就可以，不需要写得很完整。");
      return;
    }
    setBusy(true);
    setError("");
    const kept = text;
    try {
      const { res, data } = await apiJson("/api/reading/checkin/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          original_text: kept,
          regenerate,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "AI暂时没有回应，请稍后再试。");
      setItem(data.item);
      setAiText(data.item?.ai_text || "");
      setText(data.item?.original_text || kept);
      setShowCard(false);
    } catch (err) {
      setText(kept);
      setError(err.message || "AI暂时没有回应，请稍后再试。");
    } finally {
      setBusy(false);
    }
  };

  const makeCard = async (tpl = template, photoUrl = headerPhotoUrl) => {
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/reading/checkin/card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          template: tpl,
          header_image_url: photoUrl || "",
        }),
      });
      if (!res.ok) {
        setError(data?.detail || "内容已经保存，分享卡片稍后可以重新生成。");
        return;
      }
      setItem(data.item);
      setTemplate(normalizeTemplate(data.item?.card_template || tpl));
      if (data.item?.card_image_url !== undefined) {
        setHeaderPhotoUrl(data.item.card_image_url || "");
      }
      setCompleted(true);
      setShowCard(true);
      setShareHint("");
      load();
    } catch (err) {
      setError("内容已经保存，分享卡片稍后可以重新生成。");
    } finally {
      setBusy(false);
    }
  };

  const uploadHeaderPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setPhotoUploading(true);
    try {
      const body = new FormData();
      body.append("user_id", String(userId));
      body.append("file", file);
      const res = await apiFetch("/api/reading/checkin/upload-header", {
        method: "POST",
        body,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "照片上传失败");
        return;
      }
      setHeaderPhotoUrl(data.url || "");
      setShowCard(true);
      await makeCard(template, data.url || "");
      setShareHint("已换成你的照片。分享出去别人更容易注意到你。");
    } catch (err) {
      setError("照片上传失败，请重试");
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const saveImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `今日收获-${view?.date || "card"}.png`;
    a.click();
  };

  const shareImage = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setShareHint("");
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const file = new File([blob], "today-harvest.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "我的今日收获" });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }
    setShareHint("长按图片保存到手机，然后分享到朋友圈。");
  };

  const bookTitle = view?.book?.title || "";
  const left = item ? item.regenerate_left : 3;

  return (
    <div className="min-h-screen bg-[#f6f1ea] flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-lg mx-auto px-4 py-6 space-y-5">
        <section className="space-y-2">
          <p className="text-xs tracking-widest text-amber-800/70">今日晨读</p>
          <h1 className="text-2xl font-bold text-stone-800">
            {view?.date_label || "今天"}
          </h1>
          {bookTitle ? (
            <p className="text-sm text-stone-600">今日共读：《{bookTitle}》</p>
          ) : null}
          {completed && (
            <p className="text-sm text-emerald-800">✓ {view?.date_label} 今日晨读已完成</p>
          )}
        </section>

        <section className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-stone-900 leading-snug">
              今天读完，你有什么感受？
            </h2>
            <p className="text-sm text-stone-500 mt-2">
              不需要写文章，一两句话就够了。也可以点麦克风说话，不用打字。
            </p>
          </div>
          <div className="relative">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 200))}
              rows={5}
              placeholder="写下今天读书后的感受，或点右下角麦克风语音留言……"
              className="w-full rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 pr-14 text-base leading-relaxed resize-none"
            />
            <div className="absolute right-2 bottom-2">
              <SpeechInputButton
                disabled={busy}
                maxLength={200}
                getPrefix={() => text}
                onResult={(value) => {
                  if (value) setText(String(value).slice(0, 200));
                }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-stone-400">可以从这些方面写：</p>
            <div className="flex flex-wrap gap-2">
              {PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setText((prev) => (prev.trim() ? prev : p + "："))}
                  className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-900 text-xs border border-amber-100"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          {error && (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              {error}
            </p>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => generate(false)}
            className="w-full py-3.5 rounded-full bg-stone-800 text-white text-base font-semibold disabled:opacity-50"
          >
            {busy ? "整理中…" : "AI生成我的今日收获"}
          </button>
        </section>

        {aiText && (
          <section className="bg-white rounded-3xl border border-stone-100 p-5 space-y-4">
            <h3 className="text-lg font-bold text-stone-900">我的今日收获</h3>
            <p className="text-lg leading-relaxed text-stone-800">{aiText}</p>
            {bookTitle && (
              <p className="text-sm text-stone-500">
                今日共读
                <br />《{bookTitle}》
              </p>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => makeCard(template)}
              className="w-full py-3.5 rounded-full bg-amber-800 text-white font-semibold disabled:opacity-50"
            >
              生成分享卡片
            </button>
            <button
              type="button"
              disabled={busy || left <= 0}
              onClick={() => generate(true)}
              className="w-full py-3 rounded-full border border-stone-200 text-stone-700 disabled:opacity-40"
            >
              重新生成{left <= 0 ? "（今日已用完）" : `（还可 ${left} 次）`}
            </button>
          </section>
        )}

        {showCard && (
          <section className="space-y-3">
            <div className="flex gap-2 overflow-x-auto pb-1 items-center">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadHeaderPhoto}
              />
              <button
                type="button"
                disabled={photoUploading || busy}
                onClick={() => photoInputRef.current?.click()}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                  headerPhotoUrl
                    ? "bg-rose-600 text-white border-rose-600"
                    : "bg-white text-rose-700 border-rose-200"
                }`}
              >
                {photoUploading ? "上传中…" : headerPhotoUrl ? "换我的照片" : "用我的照片"}
              </button>
              {CARD_TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setTemplate(t.key);
                    setHeaderPhotoUrl("");
                    makeCard(t.key, "");
                  }}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                    !headerPhotoUrl && template === t.key
                      ? "bg-stone-800 text-white border-stone-800"
                      : "bg-white text-stone-600"
                  }`}
                >
                  <span
                    className="w-3.5 h-3.5 rounded-full border border-white/70"
                    style={{ background: t.swatch }}
                  />
                  {t.label}
                </button>
              ))}
            </div>
            <canvas
              ref={canvasRef}
              className="w-full h-auto rounded-2xl shadow-md bg-white"
            />
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={saveImage}
                className="py-3.5 rounded-full bg-stone-800 text-white font-semibold"
              >
                保存图片
              </button>
              <button
                type="button"
                onClick={shareImage}
                className="py-3.5 rounded-full border border-stone-300 bg-white font-semibold"
              >
                分享
              </button>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed">
              {shareHint ||
                "可点「用我的照片」把头部换成自己的照片；也可继续用五种风景。"}
            </p>
          </section>
        )}

        <section className="space-y-3 pb-6">
          <h3 className="font-semibold text-stone-800">我的晨读收获</h3>
          {history.length === 0 ? (
            <p className="text-sm text-stone-400">还没有记录。写下一句话就开始。</p>
          ) : (
            <ul className="space-y-2">
              {history.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setAiText(h.ai_text);
                      setText(h.original_text || "");
                      setItem(h);
                      setTemplate(normalizeTemplate(h.card_template || "dawn"));
                      setShowCard(true);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="w-full text-left bg-white rounded-2xl border border-stone-100 px-4 py-3"
                  >
                    <p className="text-xs text-stone-400">{h.reading_date}</p>
                    <p className="text-sm text-stone-800 mt-1 line-clamp-2">{h.ai_text}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Link to="/book-club" className="inline-block text-sm text-amber-800">
            返回读书会
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
